import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import type { ProviderReference } from '../../../../../js/api';
import { ProviderReferencesService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { Description, Field, Label } from '../../../components/fieldset';
import { STAFF_PROVIDER_QUERY_KEY, STAFF_PROVIDER_REFERENCES_QUERY_KEY } from './queryKeys';
import { requestScheduleBounds, toRequestScheduledAt } from './ReferenceFormDialog';
import 'react-datepicker/dist/react-datepicker.css';

type ReferenceScheduleDialogProps = {
  open: boolean;
  onClose: () => void;
  providerId: string;
  reference: ProviderReference | null;
};

/** Move the day the survey request goes out (today sends now; up to three months out). */
export function ReferenceScheduleDialog({
  open,
  onClose,
  providerId,
  reference,
}: ReferenceScheduleDialogProps) {
  const queryClient = useQueryClient();
  const current = reference?.responses?.[0]?.request_scheduled_at ?? null;
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const { min: minDate, max: maxDate } = requestScheduleBounds();

  useEffect(() => {
    if (open) setScheduledAt(current ? new Date(current) : new Date());
  }, [open, current]);

  const mutation = useMutation({
    mutationFn: (value: Date) =>
      ProviderReferencesService.providerReferencesScheduleRequestCreate({
        id: reference!.id,
        requestBody: { request_scheduled_at: toRequestScheduledAt(value) },
      }),
    onSuccess: () => {
      toast.success('Request date saved');
      queryClient.invalidateQueries({
        queryKey: [STAFF_PROVIDER_REFERENCES_QUERY_KEY, providerId],
      });
      queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId] });
      onClose();
    },
    onError: (error: Error) =>
      handleApiError(error, { messagePrefix: 'Error scheduling the request' }),
  });

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle className="tw:!text-2xl tw:!text-brand-green-0 tw:!mt-0 tw:!pt-0">
        Change Request Date
      </DialogTitle>
      <DialogBody>
        <Field>
          <Label>Send Request On</Label>
          <ReactDatePicker
            className="datepicker-input"
            wrapperClassName="datepicker-wrapper"
            selected={scheduledAt}
            onChange={(date) => setScheduledAt(date)}
            minDate={minDate}
            maxDate={maxDate}
            placeholderText="Select a date"
            dateFormat="MM/dd/yyyy"
            disabled={mutation.isPending}
          />
          <Description>
            Today sends the survey right away; a later date goes out that morning.
          </Description>
        </Field>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="zinc-outline" onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          color="brandgreen1"
          onClick={() => scheduledAt && mutation.mutate(scheduledAt)}
          disabled={mutation.isPending || !scheduledAt}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
